import { useEffect, useState } from 'react';
import { Button, Checkbox, Grid, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Estate_FieldsFragment, EstateType } from 'queries';

import EstateDocumentUploadBox from './estate-document-upload-box';

type Props = {
  handleBack: () => void;
  handleNext: () => void;
  onOrderSearchButtonClick?: () => void;
  estate: Estate_FieldsFragment;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    h5: {
      paddingTop: '0px',
      paddingBottom: '15px',
    },
    noDocumentCheckbox: {
      color: 'black',
    },
    noDocumentSpan: {
      paddingTop: '12px',
    },
    previousButton: {
      width: '30%',
      borderRadius: '25px',
      marginRight: theme.spacing(5),
      backgroundColor: 'white',
      color: 'black',
      border: '1px solid black',
    },
    nextButton: {
      width: '30%',
      borderRadius: '25px',
      backgroundColor: '#5ED1F0',
      color: 'black',
    },
  }),
);

export default function EstateDocumentsForm({
  handleBack,
  handleNext,
  estate,
  onOrderSearchButtonClick,
}: Props) {
  const classes = useStyles();
  const [noLettersTestamentaryCheckboxSelected, setNoLettersTestamentaryCheckboxSelected] =
    useState<boolean>(false);
  const [noPhotoIdCheckboxSelected, setNoPhotoIdCheckboxSelected] = useState<boolean>(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true); // by default next button is disabled when the stepper loads
  const [orderSearchButtonDisabled, setOrderSearchButtonDisabled] = useState<boolean>(true);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const noLettersTestamentaryCheckboxOnChange = (e: any) => {
    // TRUADMIN
    const isSelected = e.target.checked;
    setNoLettersTestamentaryCheckboxSelected(isSelected);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const noPhotoIdCheckboxOnChange = (e: any) => {
    // TRUPLANNING and TRUPROXY
    const isSelected = e.target.checked;
    setNoPhotoIdCheckboxSelected(isSelected);
  };

  useEffect(() => {
    if (estate.type === EstateType.Truadmin || estate.type === EstateType.Trustadmin) {
      if (noLettersTestamentaryCheckboxSelected) {
        setNextButtonDisabled(estate.documentIdDeathCertificate ? false : true);
      } else {
        setNextButtonDisabled(
          estate.documentIdLettersTestamentary && estate.documentIdDeathCertificate ? false : true,
        );
        setOrderSearchButtonDisabled(
          estate.documentIdLettersTestamentary && estate.documentIdDeathCertificate ? false : true,
        );
      }
      return;
    }

    if (estate.type === EstateType.Truplanning) {
      if (noPhotoIdCheckboxSelected) {
        setNextButtonDisabled(false);
        setOrderSearchButtonDisabled(false);
      } else {
        setNextButtonDisabled(
          estate.documentIdFrontOfPhotoId && estate.documentIdBackOfPhotoId ? false : true,
        );
        setOrderSearchButtonDisabled(
          estate.documentIdFrontOfPhotoId && estate.documentIdBackOfPhotoId ? false : true,
        );
      }
      return;
    }

    if (estate.type === EstateType.Truproxy) {
      if (noPhotoIdCheckboxSelected) {
        setNextButtonDisabled(estate.documentIdLegalDocumentation ? false : true);
        setOrderSearchButtonDisabled(false);
      } else {
        setNextButtonDisabled(
          estate.documentIdFrontOfPhotoId &&
            estate.documentIdBackOfPhotoId &&
            estate.documentIdLegalDocumentation
            ? false
            : true,
        );
        setOrderSearchButtonDisabled(
          estate.documentIdFrontOfPhotoId &&
            estate.documentIdBackOfPhotoId &&
            estate.documentIdLegalDocumentation
            ? false
            : true,
        );
      }
      return;
    }
  }, [estate, noLettersTestamentaryCheckboxSelected, noPhotoIdCheckboxSelected]);

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        Please upload the following:
      </Typography>
      <Grid container>
        {(estate.type === EstateType.Truadmin || estate.type === EstateType.Trustadmin) && (
          <>
            <EstateDocumentUploadBox
              documentId={estate.documentIdLettersTestamentary || ''}
              estateDocumentField="documentIdLettersTestamentary"
              estate={estate}
            />
            <EstateDocumentUploadBox
              documentId={estate.documentIdDeathCertificate || ''}
              estateDocumentField="documentIdDeathCertificate"
              estate={estate}
            />
            <Grid container justifyContent="center">
              <Checkbox
                color="primary"
                className={classes.noDocumentCheckbox}
                checked={noLettersTestamentaryCheckboxSelected}
                onChange={noLettersTestamentaryCheckboxOnChange}
              />
              <span className={classes.noDocumentSpan}>I don't have their Legal Documentation</span>
            </Grid>
          </>
        )}
        {estate.type === EstateType.Truplanning && (
          <>
            <EstateDocumentUploadBox
              documentId={estate.documentIdFrontOfPhotoId || ''}
              estateDocumentField="documentIdFrontOfPhotoId"
              estate={estate}
            />
            <EstateDocumentUploadBox
              documentId={estate.documentIdBackOfPhotoId || ''}
              estateDocumentField="documentIdBackOfPhotoId"
              estate={estate}
            />
          </>
        )}
        {estate.type === EstateType.Truproxy && (
          <>
            <EstateDocumentUploadBox
              documentId={estate.documentIdFrontOfPhotoId || ''}
              estateDocumentField="documentIdFrontOfPhotoId"
              estate={estate}
            />
            <EstateDocumentUploadBox
              documentId={estate.documentIdBackOfPhotoId || ''}
              estateDocumentField="documentIdBackOfPhotoId"
              estate={estate}
            />
            <EstateDocumentUploadBox
              documentId={estate.documentIdLegalDocumentation || ''}
              estateDocumentField="documentIdLegalDocumentation"
              estate={estate}
            />
          </>
        )}
      </Grid>
      {(estate.type === EstateType.Truplanning || estate.type === EstateType.Truproxy) && (
        <Grid container justifyContent="center">
          <Checkbox
            color="primary"
            className={classes.noDocumentCheckbox}
            checked={noPhotoIdCheckboxSelected}
            onChange={noPhotoIdCheckboxOnChange}
          />
          <span className={classes.noDocumentSpan}>I don't have their ID</span>
        </Grid>
      )}
      <Button
        className={classes.previousButton}
        color="primary"
        onClick={() => {
          handleBack();
        }}
        variant="contained"
      >
        Previous
      </Button>
      <Button
        className={classes.previousButton}
        onClick={() => {
          handleNext();
        }}
        disabled={nextButtonDisabled && !noLettersTestamentaryCheckboxSelected}
        color="secondary"
        variant="contained"
      >
        Go to Estate Dashboard
      </Button>
      {onOrderSearchButtonClick && (
        <Button
          className={classes.nextButton}
          disabled={orderSearchButtonDisabled}
          color="secondary"
          variant="contained"
          onClick={onOrderSearchButtonClick}
        >
          Run Search
        </Button>
      )}
    </>
  );
}
